import { Toaster } from "sonner";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { cn } from "@/ui/kit/cn";
import { Kbd } from "@/ui/kit/kbd";
import { TooltipProvider } from "@/ui/kit/tooltip";
import { CommandPalette } from "./dialogs/command-palette";
import { ExportDialog } from "./dialogs/export";
import { PresetsDialog } from "./dialogs/presets";
import { ShortcutsDialog } from "./dialogs/shortcuts";
import { useBoot } from "./hooks/boot";
import { useDogfood } from "./hooks/dogfood";
import { DropZone } from "./hooks/drop";
import { useHotkeys } from "./hooks/hotkeys";
import { Inspector } from "./layout/inspector";
import { Sidebar } from "./layout/sidebar";
import { AdjustTool, GenerateTool } from "./layout/tools";
import { TopBar } from "./layout/topbar";
import { PREVIEW_TABS, Stage } from "./previews/stage";

function PreviewTabs() {
  const tab = useEditor((s) => s.previewTab);
  const set = useEditor((s) => s.set);
  return (
    <div className="flex items-center gap-1 px-4 pt-3">
      <div
        role="tablist"
        className="flex flex-wrap gap-0.5 rounded-lg border border-line bg-panel p-0.5"
      >
        {PREVIEW_TABS.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => set({ previewTab: t.id })}
            title={`${t.label} (${i + 1})`}
            className={cn(
              "h-7 rounded-md px-2.5 text-xs transition-colors",
              tab === t.id ? "bg-raised text-fg shadow-sm" : "text-muted hover:text-fg",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <span className="flex-1" />
      <span className="hidden text-[11px] text-faint xl:inline">
        <Kbd>Alt</Kbd> + click a preview element to edit its color
      </span>
    </div>
  );
}

function RightPanel() {
  const tool = useUi((s) => s.tool);
  return (
    <aside className="flex h-full min-h-0 w-[22rem] shrink-0 flex-col border-line border-l bg-panel">
      {tool === "generate" ? <GenerateTool /> : tool === "adjust" ? <AdjustTool /> : <Inspector />}
    </aside>
  );
}

export function App() {
  const ready = useBoot();
  useHotkeys();
  useDogfood();
  return (
    <TooltipProvider>
      <div className="flex h-full min-w-[1024px] flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          <Sidebar />
          <main className="flex min-w-0 flex-1 flex-col">
            <PreviewTabs />
            <div className="min-h-0 flex-1 p-4">
              {ready ? <Stage /> : <div className="h-full animate-pulse rounded-xl bg-panel" />}
            </div>
          </main>
          <RightPanel />
        </div>
      </div>
      <ExportDialog />
      <PresetsDialog />
      <ShortcutsDialog />
      <CommandPalette />
      <DropZone />
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{ className: "!bg-raised !border-line !text-fg" }}
      />
    </TooltipProvider>
  );
}
